import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { AppSelect } from '../../../components/common/select';
import useModalEffect from '../../../hooks/useModalEffect';

const MODULE_OPTIONS = [
  { value: 'Invoice', label: 'Invoice' },
  { value: 'PO', label: 'Purchase Order' },
  { value: 'Delivery', label: 'Delivery Note' },
  { value: 'Leads', label: 'Leads' },
  { value: 'Proposal', label: 'Proposal' },
];
const RESET_PERIOD_OPTIONS = [
  { value: 'yearly', label: 'Yearly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'never', label: 'Never' },
];

function generateExample({
  suffixCode = '',
  departmentCode = '',
  includeYears = false,
  includeMonths = false,
  digitLength = 4,
  startingNumber = 1,
}) {
  const year = includeYears ? new Date().getFullYear() : null;
  const month = includeMonths ? String(new Date().getMonth() + 1).padStart(2, '0') : null;
  let number = String(startingNumber).padStart(digitLength, '0');
  let parts = [departmentCode];
  if (year) parts.push(year);
  if (month) parts.push(month);
  parts.push(number);
  let result = parts.filter(Boolean).join('-');
  if (suffixCode) result = result + '-' + suffixCode;
  return result;
}

const CreateFormatNumberForm = ({ isOpen, onClose, onSubmit, initialData }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState({
    name: '',
    departmentCode: '',
    includeYears: true,
    includeMonths: true,
    digitLength: 4,
    startingNumber: 1,
    moduleName: '',
    suffixCode: '',
    resetPeriod: 'yearly',
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        departmentCode: initialData.departmentCode || '',
        includeYears: initialData.includeYears !== undefined ? initialData.includeYears : true,
        includeMonths: initialData.includeMonths !== undefined ? initialData.includeMonths : true,
        digitLength: initialData.digitLength || 4,
        startingNumber: initialData.startingNumber || 1,
        moduleName: initialData.moduleName || '',
        suffixCode: initialData.suffixCode || '',
        resetPeriod: initialData.resetPeriod || 'yearly',
      });
    } else {
      setFormData({
        name: '',
        departmentCode: '',
        includeYears: true,
        includeMonths: true,
        digitLength: 4,
        startingNumber: 1,
        moduleName: '',
        suffixCode: '',
        resetPeriod: 'yearly',
      });
    }
    setFormErrors({});
  }, [initialData, isOpen]);

  // Modal animation effects
  ;

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining modal states when component unmounts
      const cleanupModal = () => {
        // Remove modal scroll lock
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-backdrop');
      };
      
      cleanupModal();
    };
  }, []);


  // Cleanup effect: reset modal body state when component unmounts.
  useEffect(() => {
    return () => {
      // React owns the backdrop DOM (createPortal); only reset body state here.
      // Remove modal-open class from body
      document.body.classList.remove('modal-open');
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRadioChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value === 'true' }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.departmentCode.trim()) {
      errors.departmentCode = 'Department Code is required';
    }
    if (!formData.moduleName) {
      errors.moduleName = 'Module Name is required';
    }
    if (!formData.suffixCode.trim()) {
      errors.suffixCode = 'Suffix Code is required';
    }
    if (!formData.digitLength || formData.digitLength < 1) {
      errors.digitLength = 'Digit Length must be at least 1';
    }
    if (!formData.startingNumber || formData.startingNumber < 0) {
      errors.startingNumber = 'Starting Number must be at least 0';
    }
    if (!formData.resetPeriod) {
      errors.resetPeriod = 'Reset Period is required';
    }
    return errors;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      onSubmit(formData);
    } else {
      setFormErrors(errors);
    }
  };

  const handleModalClose = () => {
    setFormData({
      name: '',
      departmentCode: '',
      includeYears: true,
      includeMonths: true,
      digitLength: 4,
      startingNumber: 1,
      moduleName: '',
      suffixCode: '',
      resetPeriod: 'yearly',
    });
    setFormErrors({});
    onClose();
  };

  const exampleOutput = generateExample(formData);

  return (
    <>
      <style>{`
        #modal-create-format-number .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-create-format-number .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-create-format-number .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-create-format-number .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-create-format-number .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-create-format-number .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
        id="modal-create-format-number"
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{initialData ? 'Edit Format Number' : 'Create Format Number'}</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="inv-section-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  Details
                </div>
                <div className="mb-3">
                  <label className="form-label">Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter format name"
                    required
                  />
                  {formErrors.name && (
                    <div className="invalid-feedback">{formErrors.name}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Department Code <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.departmentCode ? 'is-invalid' : ''}`}
                    name="departmentCode"
                    value={formData.departmentCode}
                    onChange={handleInputChange}
                    placeholder="Enter department code"
                    required
                  />
                  {formErrors.departmentCode && (
                    <div className="invalid-feedback">{formErrors.departmentCode}</div>
                  )}
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Include Years</label>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="includeYears"
                          id="includeYearsYes"
                          value="true"
                          checked={formData.includeYears === true}
                          onChange={() => handleRadioChange('includeYears', 'true')}
                        />
                        <label className="form-check-label" htmlFor="includeYearsYes">
                          Yes
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="includeYears"
                          id="includeYearsNo"
                          value="false"
                          checked={formData.includeYears === false}
                          onChange={() => handleRadioChange('includeYears', 'false')}
                        />
                        <label className="form-check-label" htmlFor="includeYearsNo">
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Include Months</label>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="includeMonths"
                          id="includeMonthsYes"
                          value="true"
                          checked={formData.includeMonths === true}
                          onChange={() => handleRadioChange('includeMonths', 'true')}
                        />
                        <label className="form-check-label" htmlFor="includeMonthsYes">
                          Yes
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="includeMonths"
                          id="includeMonthsNo"
                          value="false"
                          checked={formData.includeMonths === false}
                          onChange={() => handleRadioChange('includeMonths', 'false')}
                        />
                        <label className="form-check-label" htmlFor="includeMonthsNo">
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Digit Length <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${formErrors.digitLength ? 'is-invalid' : ''}`}
                    name="digitLength"
                    value={formData.digitLength}
                    onChange={handleInputChange}
                    placeholder="Enter digit length"
                    min={1}
                    required
                  />
                  {formErrors.digitLength && (
                    <div className="invalid-feedback">{formErrors.digitLength}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Starting Number <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${formErrors.startingNumber ? 'is-invalid' : ''}`}
                    name="startingNumber"
                    value={formData.startingNumber}
                    onChange={handleInputChange}
                    placeholder="Enter starting number"
                    min={0}
                    required
                  />
                  {formErrors.startingNumber && (
                    <div className="invalid-feedback">{formErrors.startingNumber}</div>
                  )}
                </div>
                <div className="mb-3">
                  <AppSelect
                    name="moduleName"
                    label="Module Name"
                    value={formData.moduleName}
                    onChange={handleSelectChange}
                    options={MODULE_OPTIONS.map(opt => ({ id: opt.value, name: opt.label }))}
                    placeholder="Select module"
                    error={formErrors.moduleName}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Suffix Code <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.suffixCode ? 'is-invalid' : ''}`}
                    name="suffixCode"
                    value={formData.suffixCode}
                    onChange={handleInputChange}
                    placeholder="Enter suffix code"
                    required
                  />
                  {formErrors.suffixCode && (
                    <div className="invalid-feedback">{formErrors.suffixCode}</div>
                  )}
                </div>
                <div className="mb-3">
                  <AppSelect
                    name="resetPeriod"
                    label="Reset Period"
                    value={formData.resetPeriod}
                    onChange={handleSelectChange}
                    options={RESET_PERIOD_OPTIONS.map(opt => ({ id: opt.value, name: opt.label }))}
                    placeholder="Select reset period"
                    error={formErrors.resetPeriod}
                    required
                  />
                </div>
              </div>
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  <span style={{ color: '#9aa5b1' }}>
                    <span className="inv-required-star">*</span> Required fields
                  </span>
                </span>
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-inv-save">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

CreateFormatNumberForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object
};

export default CreateFormatNumberForm; 